var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { FaPlus, FaTrash, FaImage, FaCheck, FaTimes } from 'react-icons/fa';
import { PrizeType, ScannableResourceSelectionType, ABILITY_COLORS } from '../../api/models';
import { backendUrl } from '@common/contracts';
import { getNodeTypes } from '../../api/node-types';
import './StampTool.scss';
import { getResources } from '../../api/resources';
// Available properties that can be added to stamp
const AVAILABLE_PROPERTIES = [
    {
        id: 'nodeType',
        label: 'Node Type'
    },
    {
        id: 'scannableResources',
        label: 'Scannable Resources'
    },
    {
        id: 'prize',
        label: 'Loot'
    },
    {
        id: 'randomEncounters',
        label: 'Random Encounters'
    }
];
// Common resources array for selection (used for scannable resources and prizes)
// Static resource list removed
// Node types loaded dynamically
// Use custom labels for prize selection: Coins, Knowledge, Resource
const prizeTypeOptions = [
    { value: PrizeType.Money, label: 'Coins' },
    { value: PrizeType.Knowledge, label: 'Knowledge' },
    { value: PrizeType.Resource, label: 'Resource' }
];
// Helper function to get node type image URL
const getNodeTypeImageUrl = (nodeType) => {
    if (!(nodeType === null || nodeType === void 0 ? void 0 : nodeType.image))
        return null;
    return nodeType.image.startsWith('http') ? nodeType.image : `${backendUrl}${nodeType.image}`;
};
// Helper to render characteristic badges 
const renderCharacteristicBadges = (event) => {
    return (_jsx("div", { className: "characteristic-badges", children: event.encounters.map((option, index) => ( // Changed from LevelEvent to any
        _jsxs("span", { className: "characteristic-badge", style: { backgroundColor: ABILITY_COLORS[option.ability] }, children: [option.ability, " ", option.difficulty] }, index))) }));
};
// Helper to get event image URL
const getEventImageUrl = (event) => {
    if (event.cardImage) {
        return `${backendUrl}/uploads/events/${event.cardImage}`;
    }
    return null;
};
// The StampTool component
export const StampTool = ({ onApplyStamp, onCursorChange: _onCursorChange, shouldReset = false, stampEnabled = false }) => {
    var _a, _b, _c, _d, _e;
    // State for the stamp configuration
    const [stampConfig, setStampConfig] = useState({});
    // Node types state
    const [nodeTypes, setNodeTypes] = useState([]);
    // Load node types on mount
    useEffect(() => {
        const load = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const nts = yield getNodeTypes();
                setNodeTypes(nts.filter(t => !t.isArchived));
            }
            catch (e) {
                console.error('Error loading node types', e);
            }
        });
        load();
    }, []);
    // Stamp mode - now we use the external state
    const [stampMode, setStampMode] = useState(false);
    // State for tracking which property sections are visible
    const [activeProperties, setActiveProperties] = useState([]);
    // State for property selection menu
    const [showPropertySelector, setShowPropertySelector] = useState(false);
    // Initialize state for stamp options
    const [scannableSelectionType, setScannableSelectionType] = useState(ScannableResourceSelectionType.All);
    const [randomResourceCount, setRandomResourceCount] = useState(5);
    const [selectedResources, setSelectedResources] = useState([]);
    const [availableEncounters] = useState([]); // Changed from LevelEvent to any
    // State and effect for loading real resources
    const [availableResources, setAvailableResources] = useState([]);
    useEffect(() => {
        const fetchResources = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const resources = yield getResources();
                const activeResources = resources.filter(r => !r.isArchived);
                setAvailableResources(activeResources.map(r => ({ id: r.id.toString(), name: r.name })));
            }
            catch (error) {
                console.error('Error loading resources:', error);
            }
        });
        fetchResources();
    }, []);
    // Alias for compatibility: use the loaded resource list
    const AVAILABLE_RESOURCES = availableResources;
    // Effect to reset the configuration when the external shouldReset flag is set
    useEffect(() => {
        if (shouldReset) {
            // Reset the stamp configuration
            setStampConfig({});
            setActiveProperties([]);
            // Do not change the cursor mode in order to preserve its current state
        }
    }, [shouldReset]);
    // Sync the internal state with the external one
    useEffect(() => {
        setStampMode(stampEnabled);
    }, [stampEnabled]);
    // --- Event Handlers ---
    // Toggle property selector menu
    const togglePropertySelector = () => {
        setShowPropertySelector(!showPropertySelector);
    };
    // Add a property to the active properties list
    const addProperty = (propertyId) => {
        var _a;
        if (!activeProperties.includes(propertyId)) {
            setActiveProperties([...activeProperties, propertyId]);
        }
        // Initialize values based on property
        if (propertyId === 'scannableResources' && !stampConfig.hasScannableResources) {
            const allResourceIds = AVAILABLE_RESOURCES.map(r => r.id);
            setScannableSelectionType(ScannableResourceSelectionType.All);
            setSelectedResources(allResourceIds);
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { hasScannableResources: true, scannableResourcesType: ScannableResourceSelectionType.All, scannableResourcesList: allResourceIds })));
        }
        else if (propertyId === 'randomEncounters' && !stampConfig.randomEncounters) {
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { randomEncounters: {
                    enabled: true,
                    probability: 10,
                    encounterSelection: 'all',
                    selectedEncounters: availableEncounters.map(e => e.id)
                } })));
        }
        else if (propertyId === 'nodeType' && !stampConfig.typeId) {
            const firstId = (_a = nodeTypes[0]) === null || _a === void 0 ? void 0 : _a.id;
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { typeId: firstId !== null && firstId !== void 0 ? firstId : null })));
        }
        // Close the property selector
        setShowPropertySelector(false);
    };
    // Remove a property from the active properties list
    const removeProperty = (propertyId) => {
        setActiveProperties(activeProperties.filter(id => id !== propertyId));
        // Remove values from stampConfig
        if (propertyId === 'nodeType') {
            setStampConfig(prev => {
                const newConfig = Object.assign({}, prev);
                delete newConfig.typeId;
                return newConfig;
            });
        }
        else if (propertyId === 'scannableResources') {
            setStampConfig(prev => {
                const newConfig = Object.assign({}, prev);
                delete newConfig.hasScannableResources;
                delete newConfig.scannableResourcesType;
                delete newConfig.scannableResourcesList;
                return newConfig;
            });
        }
        else if (propertyId === 'prize') {
            setStampConfig(prev => {
                const newConfig = Object.assign({}, prev);
                delete newConfig.prizesWithoutTrial;
                return newConfig;
            });
        }
        else if (propertyId === 'randomEncounters') {
            setStampConfig(prev => {
                const newConfig = Object.assign({}, prev);
                delete newConfig.randomEncounters;
                return newConfig;
            });
        }
    };
    // Check if a property is available to add
    const isPropertyAvailable = (propertyId) => {
        return !activeProperties.includes(propertyId);
    };
    const handleResourceToggle = (event) => {
        const newValue = event.target.checked;
        if (newValue) {
            // By default, select "All" and all resources
            const allResourceIds = AVAILABLE_RESOURCES.map(r => r.id);
            setScannableSelectionType(ScannableResourceSelectionType.All);
            setSelectedResources(allResourceIds);
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { hasScannableResources: newValue, scannableResourcesType: ScannableResourceSelectionType.All, scannableResourcesList: allResourceIds })));
        }
        else {
            // Just update the flag
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { hasScannableResources: newValue })));
        }
    };
    // Handler for resource selection type change
    const handleSelectionTypeChange = (e) => {
        const newType = e.target.value;
        setScannableSelectionType(newType);
        let resourcesList = [];
        // Generate resource list based on selection type
        if (newType === ScannableResourceSelectionType.All) {
            resourcesList = AVAILABLE_RESOURCES.map(r => r.id);
        }
        else if (newType === ScannableResourceSelectionType.Random) {
            resourcesList = getRandomResources(randomResourceCount);
        }
        else {
            // For Custom selection, keep current selection
            resourcesList = selectedResources;
        }
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { scannableResourcesType: newType, scannableResourcesList: resourcesList })));
    };
    // Generate random resources
    const getRandomResources = (count) => {
        const allResources = AVAILABLE_RESOURCES.map(r => r.id);
        // Shuffle and take the first count elements
        return [...allResources].sort(() => 0.5 - Math.random()).slice(0, Math.min(count, allResources.length));
    };
    // Handler for random resource count change
    const handleRandomCountChange = (e) => {
        const count = parseInt(e.target.value, 10);
        if (!isNaN(count) && count > 0 && count <= AVAILABLE_RESOURCES.length) {
            setRandomResourceCount(count);
        }
    };
    // Handler for random resource generation
    const handleRandomizeResources = () => {
        const randomResources = getRandomResources(randomResourceCount);
        setSelectedResources(randomResources);
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { scannableResourcesList: randomResources })));
    };
    // Handler for toggling a specific resource
    const handleResourceSelection = (resourceId, isSelected) => {
        const newSelection = isSelected
            ? [...selectedResources, resourceId]
            : selectedResources.filter(id => id !== resourceId);
        setSelectedResources(newSelection);
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { scannableResourcesList: newSelection })));
    };
    // Handle changing prize type (Money, Knowledge, Resource)
    const handlePrizeTypeChange = (e, prizeIndex) => {
        const selectedType = e.target.value;
        const currentPrizes = stampConfig.prizesWithoutTrial || [];
        // Create a new array with the updated prize at the specific index
        const updatedPrizes = [...currentPrizes];
        updatedPrizes[prizeIndex] = Object.assign(Object.assign({}, updatedPrizes[prizeIndex]), { type: selectedType, resourceName: selectedType === PrizeType.Resource ? AVAILABLE_RESOURCES[0].id : undefined });
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { prizesWithoutTrial: updatedPrizes })));
    };
    // Handle choosing specific resource when Resource prize selected
    const handleResourceNameChange = (e, prizeIndex) => {
        const newResource = e.target.value;
        const currentPrizes = stampConfig.prizesWithoutTrial || [];
        if (currentPrizes[prizeIndex]) {
            const updatedPrizes = [...currentPrizes];
            updatedPrizes[prizeIndex] = Object.assign(Object.assign({}, updatedPrizes[prizeIndex]), { resourceName: newResource });
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { prizesWithoutTrial: updatedPrizes })));
        }
    };
    const handlePrizeAmountChange = (event, prizeIndex) => {
        const newAmount = parseInt(event.target.value, 10);
        const currentPrizes = stampConfig.prizesWithoutTrial || [];
        if (!isNaN(newAmount) && currentPrizes[prizeIndex]) {
            const updatedPrizes = [...currentPrizes];
            updatedPrizes[prizeIndex] = Object.assign(Object.assign({}, updatedPrizes[prizeIndex]), { amount: newAmount });
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { prizesWithoutTrial: updatedPrizes })));
        }
    };
    const handleAddPrize = () => {
        // Get available prize types by filtering out types that already exist
        // Except Resource which can be added multiple times
        const currentPrizes = stampConfig.prizesWithoutTrial || [];
        const existingTypes = currentPrizes.map(prize => prize.type);
        // Determine which prize type to add
        let prizeTypeToAdd;
        if (!existingTypes.includes(PrizeType.Money)) {
            prizeTypeToAdd = PrizeType.Money;
        }
        else if (!existingTypes.includes(PrizeType.Knowledge)) {
            prizeTypeToAdd = PrizeType.Knowledge;
        }
        else {
            prizeTypeToAdd = PrizeType.Resource;
        }
        // Create new prize
        const newPrize = {
            type: prizeTypeToAdd,
            amount: 10,
            resourceName: prizeTypeToAdd === PrizeType.Resource ? AVAILABLE_RESOURCES[0].id : undefined
        };
        // Add it to the array
        const updatedPrizes = [...currentPrizes, newPrize];
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { prizesWithoutTrial: updatedPrizes })));
        // If prize property wasn't already active, add it
        if (!activeProperties.includes('prize')) {
            setActiveProperties([...activeProperties, 'prize']);
        }
    };
    const handleRemovePrize = (prizeIndex) => {
        const currentPrizes = stampConfig.prizesWithoutTrial || [];
        // Remove the prize at the specified index
        const updatedPrizes = currentPrizes.filter((_, index) => index !== prizeIndex);
        // If array is empty, set to undefined
        if (updatedPrizes.length === 0) {
            setStampConfig(prev => {
                const newConfig = Object.assign({}, prev);
                delete newConfig.prizesWithoutTrial;
                return newConfig;
            });
            // Also remove from active properties
            removeProperty('prize');
        }
        else {
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { prizesWithoutTrial: updatedPrizes })));
        }
    };
    // Check if add prize button should be shown
    const canAddMorePrizes = () => {
        const currentPrizes = stampConfig.prizesWithoutTrial || [];
        const hasMoneyPrize = currentPrizes.some(prize => prize.type === PrizeType.Money);
        const hasKnowledgePrize = currentPrizes.some(prize => prize.type === PrizeType.Knowledge);
        // Check available resource types
        const usedResourceNames = currentPrizes
            .filter(prize => prize.type === PrizeType.Resource)
            .map(prize => prize.resourceName);
        // Now checking all 40 resources
        const hasAvailableResources = AVAILABLE_RESOURCES.some(opt => !usedResourceNames.includes(opt.id));
        // Can add if any prize type or resource is still available
        return !hasMoneyPrize || !hasKnowledgePrize || hasAvailableResources;
    };
    // Handler for random encounter toggle
    const handleRandomEncounterToggle = (e) => {
        const newEnabled = e.target.checked;
        let randomEncounters;
        if (newEnabled) {
            // If enabling, set defaults
            randomEncounters = {
                enabled: true,
                probability: 10,
                encounterSelection: 'all',
                selectedEncounters: availableEncounters.map(e => e.id)
            };
        }
        else {
            // If disabling, just update enabled flag
            randomEncounters = Object.assign(Object.assign({}, stampConfig.randomEncounters), { enabled: false });
        }
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { randomEncounters })));
    };
    // Handler for encounter probability change
    const handleProbabilityChange = (e) => {
        const value = parseInt(e.target.value, 10);
        if (!isNaN(value) && value >= 0 && value <= 100) {
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { randomEncounters: Object.assign(Object.assign({}, prev.randomEncounters), { probability: value }) })));
        }
    };
    const handleEncounterSelectionChange = (e) => {
        var _a;
        const selection = e.target.value;
        if (selection === 'all' || selection === 'custom') {
            const newSelectedEncounters = selection === 'all'
                ? availableEncounters.map(e => e.id)
                : ((_a = stampConfig.randomEncounters) === null || _a === void 0 ? void 0 : _a.selectedEncounters) || [];
            setStampConfig(prev => (Object.assign(Object.assign({}, prev), { randomEncounters: Object.assign(Object.assign({}, prev.randomEncounters), { encounterSelection: selection, selectedEncounters: newSelectedEncounters }) })));
        }
    };
    // Handler for encounter selection toggle
    const handleEncounterToggle = (encounterId, checked) => {
        var _a;
        const currentSelected = ((_a = stampConfig.randomEncounters) === null || _a === void 0 ? void 0 : _a.selectedEncounters) || [];
        let newSelectedEncounters;
        if (checked) {
            newSelectedEncounters = [...currentSelected, encounterId];
        }
        else {
            newSelectedEncounters = currentSelected.filter(id => id !== encounterId);
        }
        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { randomEncounters: Object.assign(Object.assign({}, prev.randomEncounters), { selectedEncounters: newSelectedEncounters }) })));
    };
    // Call onApplyStamp when stamp is active and configuration changes
    useEffect(() => {
        if (stampMode && Object.keys(stampConfig).length > 0) {
            onApplyStamp(stampConfig);
        }
    }, [stampMode, stampConfig, onApplyStamp]);
    // Render a section title with remove button
    const renderSectionTitle = (title, propertyId) => {
        return (_jsxs("div", { className: "property-section-header", children: [_jsx("div", { className: "section-title", children: title }), _jsx("button", { className: "remove-property-button", onClick: () => removeProperty(propertyId), title: `Remove ${title}`, children: _jsx(FaTimes, {}) })] }));
    };
    // --- JSX for the form ---
    return (_jsx("div", { className: "node-properties-form stamp-tool", children: activeProperties.length === 0 ? (_jsx("div", { className: "empty-stamp-container", children: !showPropertySelector ? (_jsxs("button", { className: "big-add-property-button", onClick: togglePropertySelector, children: [_jsx(FaPlus, {}), _jsx("span", { children: "Add Property" }), _jsx("p", { className: "add-property-hint", children: "Select properties to configure for your stamp" })] })) : (_jsxs("div", { className: "property-selector-menu", children: [_jsxs("div", { className: "property-selector-header", children: [_jsx("h3", { children: "Select Property" }), _jsx("button", { className: "close-button", onClick: togglePropertySelector, children: _jsx(FaTimes, {}) })] }), _jsx("div", { className: "property-options", children: AVAILABLE_PROPERTIES.map(property => (_jsx("button", { className: `property-option ${!isPropertyAvailable(property.id) ? 'disabled' : ''}`, onClick: () => isPropertyAvailable(property.id) && addProperty(property.id), disabled: !isPropertyAvailable(property.id), children: _jsx("span", { className: "property-name", children: property.label }) }, property.id))) })] })) })) : (_jsxs("div", { className: "stamp-properties-container", children: [showPropertySelector && (_jsxs("div", { className: "property-selector-menu", children: [_jsxs("div", { className: "property-selector-header", children: [_jsx("h3", { children: "Add Property" }), _jsx("button", { className: "close-button", onClick: togglePropertySelector, children: _jsx(FaTimes, {}) })] }), _jsx("div", { className: "property-options", children: AVAILABLE_PROPERTIES.map(property => (_jsx("button", { className: `property-option ${!isPropertyAvailable(property.id) ? 'disabled' : ''}`, onClick: () => isPropertyAvailable(property.id) && addProperty(property.id), disabled: !isPropertyAvailable(property.id), children: _jsx("span", { className: "property-name", children: property.label }) }, property.id))) })] })), !showPropertySelector && (_jsxs(_Fragment, { children: [activeProperties.includes('nodeType') && (_jsxs("div", { className: "node-type-section", children: [renderSectionTitle('Node Type', 'nodeType'), _jsxs("div", { className: "control-group", children: [_jsx("label", { children: "Type:" }), _jsxs("div", { className: "node-type-selector", children: [_jsxs("select", { value: (_a = stampConfig.typeId) !== null && _a !== void 0 ? _a : '', onChange: e => {
                                                        const val = e.target.value;
                                                        setStampConfig(prev => (Object.assign(Object.assign({}, prev), { typeId: val ? val : null })));
                                                    }, className: "panel-select", children: [_jsx("option", { value: "", children: "Empty" }), nodeTypes.map(nt => (_jsx("option", { value: nt.id, children: nt.name }, nt.id)))] }), stampConfig.typeId && (_jsx("div", { className: "node-type-preview", children: (() => {
                                                        const selectedType = nodeTypes.find(nt => nt.id === stampConfig.typeId);
                                                        const imageUrl = getNodeTypeImageUrl(selectedType);
                                                        return imageUrl ? (_jsx("img", { src: imageUrl, alt: selectedType === null || selectedType === void 0 ? void 0 : selectedType.name, className: "node-type-icon" })) : (_jsx("div", { className: "no-icon", children: "No icon" }));
                                                    })() }))] })] })] })), activeProperties.includes('scannableResources') && (_jsxs("div", { className: "panel-section resource-scanner-section", children: [renderSectionTitle('Scannable Resources', 'scannableResources'), _jsxs("div", { className: "control-group checkbox-group", children: [_jsx("input", { type: "checkbox", id: "scannable-res-stamp", checked: stampConfig.hasScannableResources, onChange: handleResourceToggle }), _jsx("label", { htmlFor: "scannable-res-stamp", children: "Scannable Resources Present" })] }), stampConfig.hasScannableResources && (_jsxs("div", { className: "scannable-options", children: [_jsxs("div", { className: "selection-type-control", children: [_jsx("label", { htmlFor: "selection-type", children: "Resource Selection:" }), _jsxs("select", { id: "selection-type", value: scannableSelectionType, onChange: handleSelectionTypeChange, className: "resource-select", children: [_jsx("option", { value: ScannableResourceSelectionType.All, children: "All Resources" }), _jsx("option", { value: ScannableResourceSelectionType.Random, children: "Random Selection" }), _jsx("option", { value: ScannableResourceSelectionType.Custom, children: "Custom Selection" })] })] }), scannableSelectionType === ScannableResourceSelectionType.Random && (_jsxs("div", { className: "random-resources-control", children: [_jsxs("div", { className: "random-count-input", children: [_jsx("label", { htmlFor: "random-count", children: "Number of resources:" }), _jsx("input", { type: "number", id: "random-count", min: "1", max: AVAILABLE_RESOURCES.length, value: randomResourceCount, onChange: handleRandomCountChange })] }), _jsxs("button", { className: "randomize-button panel-button", onClick: handleRandomizeResources, children: [_jsx("svg", { viewBox: "0 0 24 24", width: "16", height: "16", fill: "currentColor", children: _jsx("path", { d: "M18 4l2 3h-3v2h3l-2 3 2 3h-3v2h3l-2 3H3v-2h12V4h3zm-4 2H3v2h11V6zm0 4H3v2h11v-2zm0 4H3v2h11v-2z" }) }), "Randomize"] })] })), scannableSelectionType === ScannableResourceSelectionType.Custom && (_jsx("div", { className: "custom-resources-selection", children: _jsx("div", { className: "resources-grid", children: AVAILABLE_RESOURCES.map(resource => (_jsxs("div", { className: "resource-checkbox-item", children: [_jsx("input", { type: "checkbox", id: `res-${resource.id}`, checked: selectedResources.includes(resource.id), onChange: (e) => handleResourceSelection(resource.id, e.target.checked) }), _jsx("label", { htmlFor: `res-${resource.id}`, title: resource.name, children: resource.name })] }, resource.id))) }) })), _jsx("div", { className: "selected-resources-summary", children: scannableSelectionType === ScannableResourceSelectionType.All ? (_jsxs("span", { className: "all-resources-label", children: ["All ", AVAILABLE_RESOURCES.length, " resources selected"] })) : (_jsxs("span", { className: "resource-count-label", children: [selectedResources.length, " of ", AVAILABLE_RESOURCES.length, " resources selected"] })) })] }))] })), activeProperties.includes('prize') && (_jsxs("div", { className: "prize-section", children: [renderSectionTitle('Loot', 'prize'), stampConfig.prizesWithoutTrial && stampConfig.prizesWithoutTrial.map((prize, index) => {
                                    var _a;
                                    return (_jsxs("div", { className: "prize-details", "data-prize-type": (_a = prizeTypeOptions.find(opt => opt.value === prize.type)) === null || _a === void 0 ? void 0 : _a.label, children: [_jsx("div", { className: "prize-type-row", children: _jsx("select", { value: prize.type, onChange: (e) => handlePrizeTypeChange(e, index), disabled: prize.type !== PrizeType.Resource && stampConfig.prizesWithoutTrial.length > 1, children: prizeTypeOptions.map(opt => (_jsx("option", { value: opt.value, children: opt.label }, opt.value))) }) }), prize.type === PrizeType.Resource && (_jsx("div", { className: "resource-name-row", children: _jsx("select", { value: prize.resourceName, onChange: (e) => handleResourceNameChange(e, index), children: AVAILABLE_RESOURCES.map(opt => (_jsx("option", { value: opt.id, children: opt.name }, opt.id))) }) })), _jsxs("div", { className: "prize-amount-row", children: [_jsx("label", { children: "Amount:" }), _jsx("input", { type: "number", value: prize.amount, onChange: (e) => handlePrizeAmountChange(e, index), min: "0", className: "prize-amount-input" })] }), _jsx("button", { onClick: () => handleRemovePrize(index), className: "prize-remove-button", title: "Remove Prize", children: _jsx(FaTrash, {}) })] }, `prize-${index}`));
                                }), (!stampConfig.prizesWithoutTrial || canAddMorePrizes()) && (_jsxs("button", { onClick: handleAddPrize, className: "panel-button icon-button add-button", title: "Add Prize", children: [_jsx(FaPlus, {}), " Add Prize"] }))] })), activeProperties.includes('randomEncounters') && (_jsxs("div", { className: "panel-section resource-scanner-section random-encounters-section", children: [renderSectionTitle('Random Encounters', 'randomEncounters'), _jsxs("div", { className: "control-group checkbox-group", children: [_jsx("input", { type: "checkbox", id: "random-encounters-stamp", checked: (_b = stampConfig.randomEncounters) === null || _b === void 0 ? void 0 : _b.enabled, onChange: handleRandomEncounterToggle }), _jsx("label", { htmlFor: "random-encounters-stamp", children: "Random Encounters Present" })] }), ((_c = stampConfig.randomEncounters) === null || _c === void 0 ? void 0 : _c.enabled) && (_jsxs("div", { className: "scannable-options", children: [_jsxs("div", { className: "selection-type-control", children: [_jsx("label", { htmlFor: "encounter-selection-stamp", children: "Encounter Selection:" }), _jsxs("select", { id: "encounter-selection-stamp", value: stampConfig.randomEncounters.encounterSelection, onChange: handleEncounterSelectionChange, className: "resource-select", children: [_jsx("option", { value: "all", children: "All Encounters" }), _jsx("option", { value: "custom", children: "Custom" })] })] }), _jsxs("div", { className: "selection-type-control", children: [_jsx("label", { htmlFor: "probability-stamp", children: "Encounter probability:" }), _jsxs("div", { className: "probability-input-container", children: [_jsx("input", { type: "number", id: "probability-stamp", min: "0", max: "100", value: stampConfig.randomEncounters.probability, onChange: handleProbabilityChange }), _jsx("span", { className: "percentage-sign", children: "%" })] })] }), stampConfig.randomEncounters.encounterSelection === 'all' ? (_jsx("div", { className: "selected-resources-summary", children: _jsxs("span", { className: "all-resources-label", children: ["All ", availableEncounters.length, " encounters selected"] }) })) : (_jsxs("div", { className: "custom-resources-selection", children: [_jsx("div", { className: "resources-grid encounters-grid", children: availableEncounters.map(encounter => {
                                                        var _a, _b;
                                                        const imageUrl = getEventImageUrl(encounter);
                                                        const isSelected = ((_b = (_a = stampConfig.randomEncounters) === null || _a === void 0 ? void 0 : _a.selectedEncounters) === null || _b === void 0 ? void 0 : _b.includes(encounter.id)) || false;
                                                        return (_jsxs("div", { className: "resource-checkbox-item", children: [_jsx("input", { type: "checkbox", id: `encounter-${encounter.id}-stamp`, checked: isSelected, onChange: (e) => handleEncounterToggle(encounter.id, e.target.checked) }), _jsxs("label", { htmlFor: `encounter-${encounter.id}-stamp`, children: [_jsx("div", { className: "encounter-image", style: imageUrl ? { backgroundImage: `url(${imageUrl})` } : undefined, children: !imageUrl && (_jsx("div", { className: "image-placeholder", children: _jsx(FaImage, {}) })) }), _jsxs("div", { className: "encounter-info", children: [_jsx("div", { className: "encounter-title", children: encounter.title }), renderCharacteristicBadges(encounter)] }), isSelected && (_jsx("div", { className: "encounter-selected-indicator", children: _jsx(FaCheck, {}) }))] })] }, encounter.id));
                                                    }) }), availableEncounters.length === 0 && (_jsx("div", { className: "no-resources-message", children: "No available encounters. Create them in \"Events\" tab." })), availableEncounters.length > 0 && (_jsx("div", { className: "selected-resources-summary", children: _jsxs("span", { className: "resource-count-label", children: [((_e = (_d = stampConfig.randomEncounters) === null || _d === void 0 ? void 0 : _d.selectedEncounters) === null || _e === void 0 ? void 0 : _e.length) || 0, " of ", availableEncounters.length, " encounters selected"] }) }))] }))] }))] })), activeProperties.length < AVAILABLE_PROPERTIES.length && (_jsxs("button", { className: "add-property-button", onClick: togglePropertySelector, children: [_jsx(FaPlus, {}), _jsx("span", { children: "Add Property" })] }))] }))] })) }));
};
